import { generateOffers } from './offerCalculationService';

/**
 * Portfolio Balance-Transfer (BT) offer service
 *
 * Given one existing loan (from the Portfolio tab's row) and the borrower
 * context (already entered on the Offers & Eligibility step), fetches the
 * "if you refinanced this loan today, which lender would give the best rate?"
 * quote. Only ROI + savings is surfaced on the Portfolio chip — full offer
 * details are shown in the all-lenders dialog on click.
 *
 * ROUTING per loan_type:
 *   personal_loan            → PL engine (real quote from generate-offers)
 *   credit_card              → PL engine (BT flow: card outstanding refinanced
 *                              as Personal Loan — real-world "Credit Card BT")
 *   business_loan*           → BL engine (via generate-offers, engine picks
 *                              by lead loan_type_id — same endpoint)
 *   home_loan                → HL engine (via generate-offers)
 *   auto_loan / vehicle_loan → market-rate config (no engine yet)
 *   consumer_loan            → market-rate config
 *   education_loan           → market-rate config
 *   gold_loan                → market-rate config
 *
 * FALLBACK: when the engine returns 0 offers (borrower doesn't qualify for a
 * fresh loan of that size/tenure) OR the loan type has no engine → single
 * synthetic "Current market ROI" pseudo-offer from
 * config/offer_engine_market_rates.php. This keeps the chip showing
 * SOMETHING useful even when the engine can't produce a real quote.
 *
 * SAVINGS: computed as
 *   (old_emi × remaining_tenure) − (new_emi × remaining_tenure) − foreclosure
 * where foreclosure = outstanding × 4% × 1.18 (GST). Pass-through 0 for
 * credit-card and HL floating-rate loans (RBI banned foreclosure charges on
 * both categories).
 */

// Market-rate reference metadata — used for the dialog transparency footer
// (source + as-of date). Kept in sync with config/offer_engine_market_rates.php.
export const MARKET_META = {
    asOf: 'Jul 2026',
    source: 'Published bank rate cards',
};

// Fallback market rates — per-lender ranges (v2 — 2026-07-01).
// Shape: { [loan_type]: { label, note, lenders: [{name, roi_min, roi_max}] } }
//
// Two purposes:
//   1. Primary fallback — for loan types with NO dedicated engine
//      (auto/vehicle, consumer, education, gold).
//   2. Secondary fallback — for engine-supported types (PL, BL, HL) when
//      the engine returns 0 offers (e.g. borrower's profile doesn't match
//      any lender range). Without this, the Portfolio chip would show
//      "No BT available", which is unhelpful — the DSA still wants an
//      indicative rate to negotiate against the current lender.
//
// Kept in sync with config/offer_engine_market_rates.php on the backend
// (duplicated here to avoid a round-trip when the engine has no better
// answer). If the backend rates change, update BOTH.
const MARKET_LENDERS = {
    auto_loan: {
        label: 'Auto Loan',
        note: 'New cars, salaried borrowers.',
        lenders: [
            { name: 'HDFC Bank',       roi_min: 9.40, roi_max: 11.50 },
            { name: 'SBI',             roi_min: 9.20, roi_max: 10.50 },
            { name: 'ICICI Bank',      roi_min: 9.35, roi_max: 11.00 },
            { name: 'Axis Bank',       roi_min: 9.50, roi_max: 11.75 },
            { name: 'Kotak Mahindra',  roi_min: 9.55, roi_max: 11.50 },
            { name: 'Bajaj Finance',   roi_min: 10.50, roi_max: 13.00 },
        ],
    },
    vehicle_loan: {
        label: 'Vehicle Loan',
        note: 'Commercial vehicles and used cars.',
        lenders: [
            { name: 'HDFC Bank',       roi_min: 9.40, roi_max: 11.50 },
            { name: 'SBI',             roi_min: 9.20, roi_max: 10.50 },
            { name: 'ICICI Bank',      roi_min: 9.35, roi_max: 11.00 },
            { name: 'Axis Bank',       roi_min: 9.50, roi_max: 11.75 },
            { name: 'Bajaj Finance',   roi_min: 10.50, roi_max: 13.00 },
        ],
    },
    consumer_loan: {
        label: 'Consumer Durable Loan',
        note: 'Bajaj Finance dominates; bank rates lower but stricter.',
        lenders: [
            { name: 'Bajaj Finance',   roi_min: 12.00, roi_max: 16.00 },
            { name: 'HDFC Bank',       roi_min: 13.00, roi_max: 15.00 },
            { name: 'HDB Financial',   roi_min: 13.50, roi_max: 17.00 },
            { name: 'ICICI Bank',      roi_min: 14.00, roi_max: 16.00 },
            { name: 'IDFC First Bank', roi_min: 14.50, roi_max: 17.00 },
        ],
    },
    education_loan: {
        label: 'Education Loan',
        note: 'Domestic study rates. PSU banks offer subsidies for select courses.',
        lenders: [
            { name: 'SBI Scholar',     roi_min: 9.55,  roi_max: 11.15 },
            { name: 'PNB Saraswati',   roi_min: 9.25,  roi_max: 11.00 },
            { name: 'Bank of Baroda',  roi_min: 9.70,  roi_max: 11.25 },
            { name: 'HDFC Credila',    roi_min: 11.25, roi_max: 13.50 },
            { name: 'Avanse',          roi_min: 11.50, roi_max: 14.00 },
        ],
    },
    gold_loan: {
        label: 'Gold Loan',
        note: 'LTV-dependent. NBFC rates higher than bank rates.',
        lenders: [
            { name: 'HDFC Bank',       roi_min: 8.75,  roi_max: 10.00 },
            { name: 'SBI',             roi_min: 8.55,  roi_max: 9.50 },
            { name: 'ICICI Bank',      roi_min: 8.95,  roi_max: 10.50 },
            { name: 'Muthoot Finance', roi_min: 9.50,  roi_max: 22.00 },
            { name: 'Manappuram',      roi_min: 9.90,  roi_max: 24.00 },
        ],
    },
    personal_loan: {
        label: 'Personal Loan',
        note: 'Rates depend heavily on CIBIL, salary, and employer category.',
        lenders: [
            { name: 'HDFC Bank',       roi_min: 10.75, roi_max: 16.00 },
            { name: 'SBI',             roi_min: 11.15, roi_max: 14.75 },
            { name: 'ICICI Bank',      roi_min: 10.85, roi_max: 16.25 },
            { name: 'Axis Bank',       roi_min: 11.25, roi_max: 17.00 },
            { name: 'Kotak Mahindra',  roi_min: 10.99, roi_max: 16.99 },
            { name: 'IDFC First Bank', roi_min: 10.75, roi_max: 20.00 },
        ],
    },
    credit_card: {
        label: 'Credit Card Refinance (as Personal Loan)',
        note: 'Card outstanding refinanced as an unsecured Personal Loan.',
        lenders: [
            { name: 'HDFC Bank',       roi_min: 10.75, roi_max: 16.00 },
            { name: 'ICICI Bank',      roi_min: 10.85, roi_max: 16.25 },
            { name: 'Bajaj Finserv',   roi_min: 11.00, roi_max: 22.00 },
            { name: 'Kotak Mahindra',  roi_min: 10.99, roi_max: 16.99 },
        ],
    },
    business_loan: {
        label: 'Business Loan',
        note: 'Unsecured. Secured (LAP) is a separate lower band.',
        lenders: [
            { name: 'HDFC Bank',       roi_min: 13.50, roi_max: 21.00 },
            { name: 'ICICI Bank',      roi_min: 14.00, roi_max: 22.00 },
            { name: 'Bajaj Finserv',   roi_min: 14.50, roi_max: 24.00 },
            { name: 'Kotak Mahindra',  roi_min: 14.00, roi_max: 19.00 },
            { name: 'Tata Capital',    roi_min: 15.00, roi_max: 22.00 },
        ],
    },
    business_loan_unsecured: {
        label: 'Business Loan — Unsecured',
        note: 'Rates depend on business vintage, turnover, and repayment track.',
        lenders: [
            { name: 'HDFC Bank',       roi_min: 14.00, roi_max: 21.00 },
            { name: 'ICICI Bank',      roi_min: 14.50, roi_max: 22.00 },
            { name: 'Bajaj Finserv',   roi_min: 15.00, roi_max: 24.00 },
            { name: 'Tata Capital',    roi_min: 15.50, roi_max: 22.00 },
            { name: 'IIFL Finance',    roi_min: 16.00, roi_max: 25.00 },
        ],
    },
    business_loan_secured: {
        label: 'Business Loan — Secured (LAP)',
        note: 'Loan against property backing. Significantly lower rates than unsecured.',
        lenders: [
            { name: 'HDFC Bank',       roi_min: 9.50,  roi_max: 12.50 },
            { name: 'ICICI Bank',      roi_min: 9.75,  roi_max: 13.00 },
            { name: 'Axis Bank',       roi_min: 9.90,  roi_max: 13.25 },
            { name: 'LIC Housing',     roi_min: 9.60,  roi_max: 12.75 },
        ],
    },
    home_loan: {
        label: 'Home Loan',
        note: 'Floating rates linked to repo. Foreclosure is 0% (RBI mandate).',
        lenders: [
            { name: 'SBI',             roi_min: 8.50,  roi_max: 9.65 },
            { name: 'HDFC Bank',       roi_min: 8.55,  roi_max: 9.35 },
            { name: 'ICICI Bank',      roi_min: 8.75,  roi_max: 9.85 },
            { name: 'Axis Bank',       roi_min: 8.75,  roi_max: 12.50 },
            { name: 'LIC Housing',     roi_min: 8.50,  roi_max: 10.35 },
            { name: 'Kotak Mahindra',  roi_min: 8.70,  roi_max: 9.80 },
        ],
    },
};

// Loan types that get a live engine call. Everything else falls back to the
// market-rate lookup above.
const ENGINE_TYPES = new Set([
    'personal_loan',
    'credit_card',      // routed to PL engine — real BT flow
    'business_loan',
    'business_loan_unsecured',
    'business_loan_secured',
    'home_loan',
]);

// Foreclosure charges by loan type (fraction of outstanding, GST inclusive).
// Home loan floating & education floating are RBI-banned zero. Credit card
// has no foreclosure — just pay the outstanding.
const FORECLOSURE_RATE = {
    personal_loan:            0.04 * 1.18,   // 4% + 18% GST
    business_loan:            0.04 * 1.18,
    business_loan_unsecured:  0.04 * 1.18,
    business_loan_secured:    0.02 * 1.18,
    auto_loan:                0.05 * 1.18,
    vehicle_loan:             0.05 * 1.18,
    consumer_loan:            0.04 * 1.18,
    home_loan:                0,            // floating-rate HL — RBI banned
    education_loan:           0,            // floating — RBI banned
    gold_loan:                0.01 * 1.18,
    credit_card:              0,            // no foreclosure on cards
};

// Real-world loan-type strings from Equifax / OCR / DSA free-text include
// many synonyms of the same underlying product. The engine + market-rate
// tables are keyed by canonical slugs — so we normalize aggressively here
// AND collapse known aliases so the caller doesn't need to know that
// "Car Loan" and "Auto Loan" and "auto-loan" and "AUTO_LOAN" are all the
// same thing to the BT engine.
const TYPE_ALIASES = {
    // Auto family — everything with wheels
    car_loan:                 'auto_loan',
    two_wheeler_loan:         'auto_loan',
    commercial_vehicle_loan:  'auto_loan',
    vehicle_loan:             'auto_loan',
    used_car_loan:            'auto_loan',
    tractor_loan:             'auto_loan',
    // Home family
    house_loan:               'home_loan',
    housing_loan:             'home_loan',
    lap:                      'home_loan',      // loan against property
    loan_against_property:    'home_loan',
    // Consumer durable synonyms
    consumer_durable_loan:    'consumer_loan',
    consumer_durable:         'consumer_loan',
    durable_loan:             'consumer_loan',
    // Business family
    bl:                       'business_loan',
    working_capital:          'business_loan',
    working_capital_loan:     'business_loan',
    msme_loan:                'business_loan',
    unsecured_business_loan:  'business_loan_unsecured',
    secured_business_loan:    'business_loan_secured',
    // Card family
    credit_card_bill:         'credit_card',
    // Other misc → treat as personal loan (safest fallback for the engine)
    other_loan:               'personal_loan',
    other:                    'personal_loan',
    kisan_credit_card:        'personal_loan',   // KCC ≈ working PL for farmers
    overdraft:                'personal_loan',
};

const normalizeType = (t) => {
    const slug = String(t || '')
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
    return TYPE_ALIASES[slug] || slug;
};

/**
 * Build a plain-number EMI from principal + roi + tenure, for savings math
 * when the offer response doesn't carry an EMI (rare — most engines fill it
 * in). Standard reducing-balance formula.
 */
const calcEmi = (principal, annualRoi, tenureMonths) => {
    if (!principal || !tenureMonths) return 0;
    const r = (Number(annualRoi) || 0) / 12 / 100;
    if (r === 0) return principal / tenureMonths;
    return (principal * r * Math.pow(1 + r, tenureMonths)) / (Math.pow(1 + r, tenureMonths) - 1);
};

/**
 * Fetch the best BT offer + full lender list for one existing loan.
 *
 * @param {number|string} leadId          Lead UUID
 * @param {Object}        loan            One row from existingLoans[]
 * @param {Object}        borrowerContext Snapshot of formData/eligibilityInputs
 *                                        with the fields the engine needs
 *                                        (salary, cibil, employer, etc.)
 * @returns {Promise<{best, all, source, error}>}
 *   best   → {lender_name, roi, emi, savings, isMarketRate}
 *   all    → array of same shape, sorted by roi ascending
 *   source → 'engine' | 'market_rate'
 *   error  → error message string if the fetch failed (best/all will be null)
 */
export const fetchBestBTOffer = async (leadId, loan, borrowerContext = {}) => {
    // Loan can arrive in TWO shapes depending on the caller:
    //   (a) DB shape         → loan_type,   outstanding_loan, remaining_tenure, interest_rate
    //   (b) Row/display shape → loanType,   outstanding,      remainingTenure,  roi
    // Portfolio's renderLoanRow passes shape (b) because that's the
    // pre-transformed row it iterates over. Accept both so no caller has
    // to convert first.
    const rawType = loan?.loan_type || loan?.loanType || '';
    const type = normalizeType(rawType);
    const outstanding = Number(loan?.outstanding_loan || loan?.outstanding || 0);
    const remainingTenure = Number(
        loan?.remaining_tenure
        || loan?.remainingTenure
        || loan?.tenure_months
        || loan?.tenureMonths
        || 0
    );
    const currentRoi = Number(loan?.interest_rate || loan?.roi || 0);
    const currentEmi = Number(loan?.emi || 0);

    // Guard: loans with no outstanding or ended tenure can't be BT'd
    if (!outstanding || outstanding < 10000 || !remainingTenure || remainingTenure < 3) {
        return { best: null, all: [], source: 'ineligible', error: null };
    }

    const foreclosureRate = FORECLOSURE_RATE[type] ?? 0.04 * 1.18;
    const foreclosure = outstanding * foreclosureRate;
    const oldTotalCost = currentEmi * remainingTenure;

    // Helper: given an offer object with roi + optional emi, produce the
    // chip-ready shape. Savings is old-EMI × months − new-EMI × months −
    // foreclosure charge. Negative savings means BT isn't worth it.
    const shapeOffer = (raw, isMarket = false) => {
        const roi = Number(raw.roi ?? raw.interest_rate ?? 0);
        const emi = Number(raw.emi ?? calcEmi(outstanding, roi, remainingTenure));
        const newTotalCost = emi * remainingTenure + foreclosure;
        return {
            lender_name: raw.lender_name || raw.bank_name || raw.label || 'Market',
            roi,
            emi: Math.round(emi),
            savings: Math.round(oldTotalCost - newTotalCost),
            isMarketRate: isMarket,
        };
    };

    // ── ENGINE CALL PATH ─────────────────────────────────────────────────
    if (ENGINE_TYPES.has(type)) {
        try {
            const payload = {
                amount: outstanding,
                tenure_months: remainingTenure,
                salary: borrowerContext.salary || borrowerContext.net_salary || null,
                cibil_score: borrowerContext.cibil_score || borrowerContext.credit_score || null,
                existing_emi: borrowerContext.existing_emi || 0,
                employer_name: borrowerContext.employer_name || null,
                salary_bank_account: borrowerContext.salary_bank_account || null,
                selected_company_ids: borrowerContext.selected_company_ids || null,
                include_eligibility: false,  // BT chip only needs ROI list
                calculate_eligibility: false,
            };
            const res = await generateOffers(leadId, payload);
            const offers = res?.data?.offers || [];
            if (offers.length === 0) {
                // Engine came up empty (borrower doesn't qualify for this
                // size/tenure fresh). Fall through to market-rate below.
            } else {
                // Shape → filter → sort ascending by ROI → dedupe by
                // lender_name (keep the FIRST = LOWEST-ROI offer per lender).
                //
                // Why dedupe: the offer engine returns one row per
                // matching lender-range slab. A single lender (e.g. HDFC)
                // often has 3-6 slabs based on loan-amount buckets,
                // tenure buckets, salary-account-vs-not, etc. Without
                // dedupe the dialog shows "HDFC 11.15% / HDFC 11.15% /
                // HDFC 11.15% / HDFC 11.35% / HDFC 11.35% / ..." which
                // is noise — the DSA only cares about the best rate each
                // lender can offer. Since we've already sorted ascending
                // by ROI, the first occurrence of a lender IS the lowest.
                const seen = new Set();
                const shaped = offers
                    .map((o) => shapeOffer(o, false))
                    .filter((o) => o.roi > 0)
                    .sort((a, b) => a.roi - b.roi)
                    .filter((o) => {
                        // Normalize lender name to catch case/whitespace
                        // variants ("HDFC" vs "HDFC " vs "hdfc").
                        const key = String(o.lender_name || '').trim().toLowerCase();
                        if (!key || seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });
                return {
                    best: shaped[0] || null,
                    all: shaped,
                    source: 'engine',
                    error: null,
                };
            }
        } catch (err) {
            // Silent — fall through to market rate. The Portfolio chip will
            // still show SOMETHING useful, and error toasts on every row
            // would be noise. Log for debugging.
            // eslint-disable-next-line no-console
            console.warn('[BT] Engine call failed for loan', loan?.id, err?.message);
        }
    }

    // ── MARKET-RATE FALLBACK ──────────────────────────────────────────────
    // Used when: loan type has no engine, OR engine returned 0 offers, OR
    // engine call threw. Now produces a REAL LIST of top 4-5 lenders per
    // loan type instead of one anonymous "market rate". Each lender's ROI
    // uses the MIN of their published range (the "if borrower has great
    // profile" rate) — the range itself is exposed via roiMin/roiMax on
    // each returned offer so the UI can show "9.4% – 11.5%" for full
    // transparency.
    const marketData = MARKET_LENDERS[type];
    if (marketData && Array.isArray(marketData.lenders) && marketData.lenders.length > 0) {
        const shaped = marketData.lenders
            .map((l) => {
                const roi = Number(l.roi_min) || 0;
                const emi = calcEmi(outstanding, roi, remainingTenure);
                const newTotalCost = emi * remainingTenure + foreclosure;
                return {
                    lender_name: l.name,
                    roi,
                    roiMin: Number(l.roi_min),
                    roiMax: Number(l.roi_max),
                    emi: Math.round(emi),
                    savings: Math.round(oldTotalCost - newTotalCost),
                    isMarketRate: true,
                };
            })
            .filter((o) => o.roi > 0)
            .sort((a, b) => a.roi - b.roi);
        return {
            best: shaped[0] || null,
            all: shaped,
            source: 'market_rate',
            marketMeta: {
                asOf: MARKET_META.asOf,
                sourceLabel: MARKET_META.source,
                loanTypeLabel: marketData.label,
                note: marketData.note,
            },
            error: null,
        };
    }

    // Unknown loan type with no engine and no market rate — nothing to show.
    return {
        best: null,
        all: [],
        source: 'unavailable',
        error: null,
    };
};

/**
 * Compute the delta a proposed BT offer would produce vs the borrower's
 * CURRENT loan. Used by the chip label and the all-lenders dialog to render
 * "Save ₹X" or "+₹X worse".
 */
export const btDelta = (currentRoi, newRoi) => {
    const delta = Number(currentRoi) - Number(newRoi);
    return {
        roiDelta: delta,             // positive = new is cheaper
        isBetter: delta > 0.25,      // <0.25% saving not worth the friction
    };
};

export default { fetchBestBTOffer, btDelta };
